import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";

const STORAGE_KIT = {
  name: "Free Storage Kit",
  description: "Everything you need to get started with Endless Storage",
  price: 0,
  includes: [
    "5 premium moving boxes (18×18×24)",
    "5 QR tracking labels",
    "Packing tape roll",
    "Bubble wrap sheet",
    "Quick start guide"
  ]
};

const SuppliesStore = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.address || !formData.city || !formData.state || !formData.zip) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields to order your free kit.",
        variant: "destructive"
      });
      return;
    }

    setIsOrdering(true);

    try {
      // For now, just simulate order processing
      // TODO: Connect to Stripe checkout or order edge function
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setOrderComplete(true);
      toast({
        title: "Order Placed!",
        description: "Your free storage kit is on its way.",
      });
    } catch (error) {
      console.error("Order error:", error);
      toast({
        title: "Order Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsOrdering(false);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
            <p className="text-muted-foreground mb-6">
              Your free storage kit will arrive in 3-5 business days.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/box/log")} className="w-full">
                Start Logging Boxes
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
            <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Get Your Free Storage Kit</h1>
          <p className="text-muted-foreground">
            Everything you need to start organizing and storing your belongings
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Kit Details */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {STORAGE_KIT.name}
                </CardTitle>
                <Badge variant="secondary" className="text-lg px-3">
                  FREE
                </Badge>
              </div>
              <CardDescription>{STORAGE_KIT.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <h4 className="font-medium mb-3">Kit Includes:</h4>
              <ul className="space-y-2">
                {STORAGE_KIT.includes.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>How it works:</strong> Order your free kit, log your boxes using our app, 
                  and when you're ready to store, schedule a pickup. First pickup is always free!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Order Form */}
          <Card>
            <CardHeader>
              <CardTitle>Shipping Information</CardTitle>
              <CardDescription>Where should we send your kit?</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleOrder} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="NY"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    placeholder="10001"
                    maxLength={5}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isOrdering}>
                  {isOrdering ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Order Free Kit"
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By ordering, you agree to receive updates about your kit and storage tips.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuppliesStore;
